import * as Sentry from '@sentry/react';

import { HOUR_MS, REACT_APP_BYPASS } from '#shared';

import { getAuthToken } from '../auth';

export class AuthTokenUnavailableError extends Error {
  constructor() {
    super('AUTH ERROR. Failed to acquire token.');
    this.name = 'AuthTokenUnavailableError';
  }
}

// Thrown when the session is rejected by the backend (401/403) and cannot be
// recovered by a forced token refresh. Surfacing a typed error (instead of a
// JSON parse error on the response body) is what lets the app show a re-auth
// state rather than a silently frozen board.
export class SessionExpiredError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`AUTH ERROR. Session expired (HTTP ${status}).`);
    this.name = 'SessionExpiredError';
    this.status = status;
  }
}

// HTTP statuses that mean "the session is no longer authenticated".
const AUTH_FAILURE_STATUSES = new Set([401, 403]);

const AUTH_SESSION_EXPIRED_SENTRY_KEY =
  'sentry.auth.session_expired.reported.v1';

// Why a lost session could not be recovered — the actionable signal. Once a
// forced refresh is in place, plain expiry is transparently recovered and never
// reported; these are the cases where recovery failed:
//   token_missing               — no token could be acquired at all.
//   refresh_failed              — the forced refresh yielded no token (FE/auth).
//   token_rejected_after_refresh — a freshly refreshed token was still 401/403'd
//                                  by the backend (points at BE/session/config).
type AuthLossReason =
  | 'token_missing'
  | 'refresh_failed'
  | 'token_rejected_after_refresh';

const tabAgeBucket = (ageMs: number): string => {
  if (ageMs < HOUR_MS) {
    return '<1h';
  }
  if (ageMs < 4 * HOUR_MS) {
    return '1-4h';
  }
  if (ageMs < 8 * HOUR_MS) {
    return '4-8h';
  }
  return '>8h';
};

/**
 * Dedicated, intentional telemetry for an unrecoverable auth-session loss.
 * Emitted once per tab/session per reason (deduped, but NOT sampled — one event
 * per affected session gives accurate incident volume). Carries tab age (time
 * since page load, NOT auth-session age — it resets on reload) so a post-load
 * degradation is distinguishable from a fresh-load failure.
 *
 * Reference: docs/decisions/ARCH-005-ui-error-detection-resilience.md
 */
const reportAuthSessionExpired = (
  reason: AuthLossReason,
  httpStatus?: number,
): void => {
  try {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    const dedupeKey = `${AUTH_SESSION_EXPIRED_SENTRY_KEY}:${reason}`;
    if (sessionStorage.getItem(dedupeKey) === '1') {
      return;
    }
    // Mark as reported for this tab/session + reason.
    sessionStorage.setItem(dedupeKey, '1');

    const ageMs = Math.round(performance.now());

    Sentry.captureMessage('auth.session_expired', {
      level: 'error',
      tags: {
        category: 'auth',
        ['auth_error']: reason,
        ['tab_age_bucket']: tabAgeBucket(ageMs),
        ...(httpStatus ? { ['http_status']: httpStatus } : {}),
      },
      fingerprint: ['auth-session-expired', reason],
      extra: { ['tab_age_ms']: ageMs },
    });
  } catch {
    // Never block auth flow on telemetry.
  }
};

const isNoisyExpectedFetchError = (error: unknown): boolean => {
  if (
    error instanceof AuthTokenUnavailableError ||
    error instanceof SessionExpiredError
  ) {
    return true;
  }

  if (error && typeof error === 'object') {
    const maybeErr = error as { name?: unknown; message?: unknown };
    const name = typeof maybeErr.name === 'string' ? maybeErr.name : '';
    const message =
      typeof maybeErr.message === 'string' ? maybeErr.message : '';

    // Browser-aborted requests (navigation, timeouts, cancelation)
    if (
      name === 'AbortError' ||
      /AbortError|signal is aborted|Fetch is aborted|The user aborted a request/i.test(
        message,
      )
    ) {
      return true;
    }

    // Transient network failures
    if (/Failed to fetch|NetworkError|Load failed/i.test(message)) {
      return true;
    }
  }

  return false;
};

export const fetchWithAuthHeaders = async (
  url: string,
  json = true,
  options?: { signal?: AbortSignal },
) => {
  try {
    const headers: HeadersInit = {};
    const authenticated = REACT_APP_BYPASS !== 'true';

    if (authenticated) {
      const token = await getAuthToken();
      if (!token) {
        // Expected state (expired session, not logged in yet). Reported once
        // per session as a dedicated, unsampled auth.session_expired event.
        reportAuthSessionExpired('token_missing');
        throw new AuthTokenUnavailableError();
      }
      headers.Authorization = token;
    }

    // `credentials: 'include'` carries the BE's cc_client_id cookie, which tells
    // apart multiple screens signed in as one account. The BE is a different
    // origin (REACT_APP_BE_API_URL is absolute), and cross-origin a browser
    // ignores Set-Cookie and never returns the cookie without this flag. It is
    // unconditional, so the bypass branch above -- no headers, still a fetch --
    // is covered too.
    // ORDERING: the BE must send Access-Control-Allow-Credentials: true FIRST.
    // Under this flag a browser rejects any response lacking that header, so an
    // FE-first deploy breaks every cross-origin request; BE-first is harmless.
    // See CommandCenterMap-BE, TMDXNC-312 design spec, section 2.1.
    let res = await fetch(url, {
      headers,
      credentials: 'include',
      signal: options?.signal,
    });

    // Auth recovery: a 401/403 body is frequently plain text ("Unauthorized")
    // or an HTML login page, so it must NEVER be parsed as JSON. Attempt one
    // in-place forced token refresh + retry; if it still fails, surface a typed
    // error and telemetry instead of freezing the board.
    if (authenticated && AUTH_FAILURE_STATUSES.has(res.status)) {
      const refreshedToken = await getAuthToken({ forceRefresh: true });
      if (refreshedToken) {
        headers.Authorization = refreshedToken;
        res = await fetch(url, {
          headers,
          credentials: 'include',
          signal: options?.signal,
        });
      }
      if (AUTH_FAILURE_STATUSES.has(res.status)) {
        // Distinguish "the refresh itself failed" from "a fresh token was still
        // rejected" — different owners (FE/auth vs BE/config), same UX.
        reportAuthSessionExpired(
          refreshedToken ? 'token_rejected_after_refresh' : 'refresh_failed',
          res.status,
        );
        throw new SessionExpiredError(res.status);
      }
    }

    if (json) {
      const rawData = await res.json();

      return rawData;
    }
    return res;
  } catch (error) {
    // Avoid noisy console.error logs for expected client/network states.
    if (!isNoisyExpectedFetchError(error)) {
      console.error('Fetch Error:', error);
    }
    throw error;
  }
};
